import type { UIObservation } from './UIObservation'

export enum PerformerTypeEnum {
  patient = 'patient',
  device = 'device',
  practitioner = 'practitioner',
  system = 'system',
}

export enum InterpretationIndicatorEnum {
  normal = 'normal',
  above = 'above',
  below = 'below',
  abnormal = 'abnormal',
  aberrant = 'aberrant',
}
export enum CountBy {
  hour = 'hour',
  day = 'day',
}

export interface ObservationCount {
  code: string
  count: number
  devices: { count: number; definition: string }[]
}

export interface UnitConversion {
  from: string
  to: string
  formula: string
}

/**
 * The value of an observation.
 */
export interface ObservationValue {
  /**
   * The value when it is a string.
   */
  valueString: string | null

  /**
   * The value when it is a boolean.
   */
  valueBoolean: boolean | null

  /**
   * The value when it is an integer.
   */
  valueInteger: number | null

  /**
   * The value when it is a floating number.
   */
  valueFloat: number | null
}

/**
 * The person or device that have performed the observation.
 */
export interface Performer {
  /**
   * The UUID of the performer entity.
   */
  id: string

  /**
   * The type of entity that performed the observation.
   */
  type: PerformerTypeEnum
}

/**
 * The device used to capture the observation.
 */
export interface IDevice {
  /**
   * The UUID of the device that was used for this observation.
   */
  id: string

  /**
   * The ID of the type of device that was used for this observation.
   */
  definition: string
}

/**
 * The result of the observation interpretation.
 */
export interface Interpretation {
  /**
   * The time which the interpretation was performed.
   */
  date: Date

  /**
   * The max severity registered during the interpretation.
   */
  severity: number

  /**
   * The indicator of the interpretation.
   */
  indicator: InterpretationIndicatorEnum
}

/**
 * An observation made on a patient or device.
 */
export interface Observation {
  /**
   * The observation's UUID.
   */
  id: string

  /**
   * The time at which the observation was made.
   */
  effectiveAt: string

  /**
   * The time at which the observation was first received by the backend.
   */
  issuedAt: string

  /**
   * Correlation ID use to link multiple observations to the same transaction.
   */
  groupId: string | null

  /**
   * The UUID of the patient on which the observation was made.
   */
  patientId: string

  /**
   * Information about the person or device that have performed the observation.
   */
  performer: Performer

  /**
   * Information about the device used to capture the observation.
   */
  device: IDevice | null

  /**
   * The UUID of the encounter during which the observation was made.
   */
  encounterId: string | null

  /**
   * The UUID of the episode of care during which the observation was made.
   */
  episodeOfCareId: string | null

  /**
   * Unique observation model code.
   */
  code: string

  /**
   * Unit of the value.
   */
  unit: string | null

  /**
   * The code that classifies the general type of observation being made.
   */
  category: string

  /**
   * The focus of the observation, when it is not about the subject of record.
   */
  focus: 'patient' | 'device'

  /**
   * The value of the observation. A single of the value fields should be defined.
   */
  value: ObservationValue

  /**
   * The date until which this observation is relevant.
   */
  retentionLimit: Date | null

  /**
   * Information about the observation's interpretation.
   */
  interpretation: Interpretation | null

  status?: string
}

export type EpisodeOfCareMetric = {
  annotationType: string
  workingDays: {
    inWindow: number
    offWindow: number
  }
  notWorkingDays: {
    inWindow: number
    offWindow: number
  }
}

export interface ObservationStoreItem {
  [code: string]: { nextPageUrl: string | null; observations: UIObservation[] }
}
